import { queryOptions } from '@tanstack/react-query';

import { SensorTypeCode } from '@/common/asset-sensor';
import { ROLE } from '@/common/role';
import type { MetSensorTimeSeriesChartRecord } from '@/common/sensors/met/met-timeseries-chart';
import { usePermission } from '@/hooks/use-permission';
import { checkAssetIdIsValid } from '@/lib/utils/asset';
import { axios } from '@/lib/utils/axios';
import { getResponse } from '@/queries/common';
import { API_URLS } from '@/queries/query.client';

export type MetSensorTimeSeriesChartResponse = {
	chartData: MetSensorTimeSeriesChartRecord[];
};

export type MetSensorTimeSeriesChartFilters = {
	from?: string;
	to?: string;
	aggregation?: 'hourly' | 'daily' | 'monthly';
	getAllFields?: boolean;
};

export async function getMetTimeSeriesChart(
	assetId: string,
	sensorId: number,
	query: MetSensorTimeSeriesChartFilters,
): Promise<MetSensorTimeSeriesChartResponse | null> {
	const response = await axios.get(`${API_URLS.ASSETS.SENSORS.MET.TIMESERIES(assetId, sensorId)}`, {
		params: query,
	});
	return getResponse<MetSensorTimeSeriesChartResponse>(response.data);
}

export const useQueryGetMetTimeSeriesChart = (
	assetId: string,
	sensorId: number | null,
	filters: MetSensorTimeSeriesChartFilters,
	enabled = true,
) => {
	const shouldRead = usePermission(ROLE.SENSORS_MET_TIMESERIES_READ);
	const isValidAssetId = checkAssetIdIsValid(assetId);
	return queryOptions({
		queryKey: ['assets', assetId, 'sensors', SensorTypeCode.MET, sensorId, 'timeseries', filters],
		queryFn: () => getMetTimeSeriesChart(assetId, sensorId as number, filters),
		enabled: enabled && isValidAssetId && !!sensorId && !!filters.from && !!filters.to && shouldRead,
	});
};
